import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Navigation, Calendar, Clock, Users, Briefcase, Euro, CheckCircle2, XCircle, Loader2, ShieldCheck, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { API } from "@/lib/api";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
    <Icon size={15} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.6} />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className="text-sm text-white/90 break-words">{value || "—"}</div>
    </div>
  </div>
);

const PublicQuote = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, quote: null });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  const load = async () => {
    setState({ loading: true, error: null, quote: null });
    try {
      const r = await axios.get(`${API}/public/quote/${token}`);
      setState({ loading: false, error: null, quote: r.data });
      setName(r.data?.name || "");
    } catch (e) {
      setState({ loading: false, error: e?.response?.status === 404 ? "notfound" : "error", quote: null });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const accept = async () => {
    setFormError(null);
    if (name.trim().length < 2) { setFormError("Veuillez saisir votre nom pour signer."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/quote/${token}/accept`, { signature_name: name.trim(), accepted: true });
      setState((s) => ({ ...s, quote: r.data }));
    } catch (e) {
      setFormError(e?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally { setBusy(false); }
  };

  const refuse = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/quote/${token}/refuse`, {});
      setState((s) => ({ ...s, quote: { ...s.quote, quote_status: "refused" } }));
    } catch (e) {
      setFormError("Une erreur est survenue. Réessayez.");
    } finally { setBusy(false); }
  };

  const q = state.quote;
  const isMad = q?.booking_type === "mad";
  const price = q?.quote_price != null ? Math.round(q.quote_price) : null;

  return (
    <div className="min-h-[70vh] bg-[#0A0A0A] pt-28 pb-20 px-4">
      <SEO title="Votre devis — VlaDriver VTC" description="Consultez et acceptez votre devis VlaDriver en ligne, sans compte." noindex />
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-2">VlaDriver VTC</div>
          <h1 className="font-serif text-3xl text-white">Votre devis</h1>
        </div>

        {state.loading && (
          <div data-testid="quote-loading" className="flex items-center justify-center py-16 text-white/50">
            <Loader2 className="animate-spin mr-2" size={18} /> Chargement…
          </div>
        )}

        {!state.loading && state.error === "notfound" && (
          <div data-testid="quote-notfound" className="text-center py-16 bg-[#141414] border border-white/10 rounded-2xl">
            <XCircle size={32} className="text-white/30 mx-auto mb-3" />
            <p className="text-white/70">Ce devis est introuvable ou a expiré.</p>
            <a href="tel:+33783977370" className="inline-flex items-center gap-2 mt-4 text-[#D4AF37] text-sm"><Phone size={14} /> +33 7 83 97 73 70</a>
          </div>
        )}

        {!state.loading && state.error === "error" && (
          <div data-testid="quote-error" className="text-center py-16 bg-[#141414] border border-white/10 rounded-2xl">
            <p className="text-white/70">Une erreur est survenue.</p>
            <button onClick={load} className="mt-4 text-[#D4AF37] text-sm underline">Réessayer</button>
          </div>
        )}

        {q && (
          <div data-testid="quote-card" className="bg-[#141414] border border-white/10 rounded-2xl p-6 md:p-8">
            {/* Price header */}
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">Offre ferme — tarif fixe</div>
                <div className="flex items-baseline gap-1.5">
                  <Euro size={20} className="text-[#D4AF37]" />
                  <span data-testid="quote-price" className="font-serif text-4xl text-white">{price}</span>
                  <span className="text-white/50 text-lg">€</span>
                </div>
                <div className="text-[11px] text-white/40 mt-1">Prix total, péages inclus.</div>
              </div>
              <StatusBadge status={q.quote_status} />
            </div>

            {/* Trip details */}
            <div className="mb-6">
              <Row icon={MapPin} label={isMad ? "Rendez-vous" : "Prise en charge"} value={q.pickup} />
              {!isMad && <Row icon={Navigation} label="Destination" value={q.destination} />}
              <Row icon={Calendar} label="Date" value={q.date} />
              <Row icon={Clock} label="Heure" value={q.time} />
              {isMad && q.duration_hours && <Row icon={Clock} label="Durée (mise à disposition)" value={`${q.duration_hours} h`} />}
              <Row icon={Users} label="Passagers" value={q.passengers} />
              <Row icon={Briefcase} label="Bagages" value={q.luggage} />
              {q.flight_number && <Row icon={Navigation} label="N° de vol" value={q.flight_number} />}
              {q.quote_note && <Row icon={ShieldCheck} label="Note du chauffeur" value={q.quote_note} />}
            </div>

            {/* Action area depends on status */}
            {q.quote_status === "quoted" && (
              <div data-testid="quote-accept-form" className="border-t border-white/10 pt-6">
                <p className="text-sm text-white/60 mb-4">
                  Pour confirmer, saisissez votre nom (signature électronique) et acceptez le devis. La course sera alors réservée.
                </p>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">Votre nom complet</label>
                <input
                  data-testid="quote-signature-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Prénom Nom"
                  className="w-full bg-[#0F1115] border border-white/10 focus:border-[#D4AF37] rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm outline-none transition-colors mb-4"
                />
                <div className="flex items-start gap-2.5 mb-5 text-sm text-white/60">
                  <ShieldCheck size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>En cliquant sur « Accepter le devis », j'accepte ce devis de <span className="text-[#D4AF37] font-semibold">{price} €</span> et je confirme la réservation (signature électronique).</span>
                </div>

                {formError && <p data-testid="quote-form-error" className="text-sm text-red-400 mb-3">{formError}</p>}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="quote-accept-btn"
                    onClick={accept}
                    disabled={busy}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black text-[11px] font-semibold uppercase tracking-[0.2em] px-6 py-3.5 rounded-full hover:bg-white transition-colors disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="animate-spin" size={16} /> : <><CheckCircle2 size={15} /> Accepter le devis</>}
                  </button>
                  <button
                    data-testid="quote-refuse-btn"
                    onClick={refuse}
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-2 text-white/50 hover:text-red-400 text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 transition-colors disabled:opacity-50"
                  >
                    Refuser
                  </button>
                </div>
              </div>
            )}

            {q.quote_status === "signed" && (
              <div data-testid="quote-accepted" className="border-t border-white/10 pt-6 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 size={28} className="text-emerald-400" />
                </div>
                <h2 className="font-serif text-xl text-white mb-1">Devis accepté ✓</h2>
                <p className="text-sm text-white/60">
                  Merci {q.signature_name}. Votre réservation est confirmée pour le <strong className="text-white/80">{q.date} à {q.time}</strong>.
                </p>
                <p className="text-xs text-white/40 mt-2">Vlad vous recontacte si besoin. Besoin d'aide ? <a href="tel:+33783977370" className="text-[#D4AF37]">+33 7 83 97 73 70</a></p>
              </div>
            )}

            {q.quote_status === "refused" && (
              <div data-testid="quote-refused" className="border-t border-white/10 pt-6 text-center">
                <XCircle size={28} className="text-red-400 mx-auto mb-2" />
                <p className="text-sm text-white/60">Vous avez refusé ce devis. Pour un nouveau devis, contactez Vlad.</p>
                <a href="tel:+33783977370" className="inline-flex items-center gap-2 mt-3 text-[#D4AF37] text-sm"><Phone size={14} /> +33 7 83 97 73 70</a>
              </div>
            )}

            {q.quote_status === "awaiting_quote" && (
              <div data-testid="quote-pending" className="border-t border-white/10 pt-6 text-center text-sm text-white/60">
                Le devis est en cours de préparation. Vous recevrez le prix très bientôt.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    quoted: { t: "À confirmer", c: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30" },
    signed: { t: "Accepté", c: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
    refused: { t: "Refusé", c: "bg-red-500/15 text-red-400 border-red-500/30" },
    awaiting_quote: { t: "En attente", c: "bg-white/10 text-white/50 border-white/15" },
  };
  const s = map[status] || map.awaiting_quote;
  return <span className={`text-[10px] uppercase tracking-[0.15em] border rounded-full px-3 py-1.5 ${s.c}`}>{s.t}</span>;
};

export default PublicQuote;
